<script lang="ts" setup>
import type { ContentScene } from '~/models/Content/ContentScene'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom.js'
import useLicenseControlStore from '~/stores/licenseControl.js'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName, ColorTheme } from '~/models/Content/Color'
import { useMonoColorTheme } from '~/composables/useColorTheme.js'
import { useAppColor } from '~/composables/useAppColor.js'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import Scene from '~/components/scene/Scene.vue'

const { locationId } = defineProps<{
  locationId: number
}>()
const theme = useMonoColorTheme(ColorTheme.GreenSeagreen)
const { checkItemLicense } = useLicenseControlStore()
const { findContents } = useContentApi()

useAppColor(ColorName.Green60)

const scene = useQuery({
  queryKey: ['scene', locationId],
  staleTime: Infinity,
  async queryFn() {
    const [location] = await findContents<ContentScene>({
      contentTypeCriterion: [ContentType.Scene],
      subtreeCriterion: [Subtree.Content, Subtree.Media],
      locationIdCriterion: [locationId]
    }, 1)

    setTitle(location.title)
    checkItemLicense(location)
    return location
  },
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(scene.data)
</script>

<template>
  <div :style="theme.style.value">
    <TopBar :item="scene.data.value" />
    <Scene
      v-if="scene.data.value"
      class="h-screen w-full bg-(--theme-60) p-4"
      :content="scene.data.value"
    />
  </div>
</template>
